import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSignature } from 'lucide-react';
import {
  fetchElectricityTransition, getDisplayErrorMessage, recordElectricityTransition, withdrawElectricityTransition,
  type ElectricityTransitionView, type TransitionMethod,
} from '@/lib/api';
import { formatCalendarDay } from '@/lib/bankWindowCopy';
import {
  TRANSITION_BASIS_LABELS, TRANSITION_EARLIEST_DAY, TRANSITION_METHOD_LABELS, defaultTransitionDate, transitionNoticeError,
  type TransitionBasis,
} from '@/lib/transitionNotice';
import form from '@/components/ui/Form.module.css';
import styles from './SitePages.module.css';

const SOURCE_LABELS = { commissioning: 'commissioning launch', session: 'first charging session', charger: 'charger registration' } as const;

function instantDay(iso: string, zone: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: zone }).format(new Date(iso));
}

/** The last full day of a window that closes at `endIso` (a midnight closes the day before). */
function lastDay(endIso: string, zone: string): string {
  return instantDay(new Date(Date.parse(endIso) - 1).toISOString(), zone);
}

/** One plain sentence on where the location stands. */
export function transitionStatusLine(v: ElectricityTransitionView): string {
  const zone = v.location.transition_zone;
  const launched = v.launch.first_at ? instantDay(v.launch.first_at, zone) : null;
  switch (v.enrollment) {
    case 'owner_site':
      return 'A home. Its owner is paid under the Payout Terms, not the Site Host Terms.';
    case 'not_launched':
      return 'Not launched yet. Its launch decides whether the Site Host Terms apply.';
    case 'existing_agreement':
      return `Launched before 2 October 2026${v.launch.legacy_v1 ? ' (a v1 site)' : ''}. The existing agreement applies and the bank countdown is hidden until a Transition Notice is recorded.`;
    case 'manual':
      return `Launched on ${launched ?? 'an unknown date'} without enrolling: the Site Host Terms were not published in the Platform, or the launch was outside commissioning. Eligible new sites use the publication bank-window catch-up from their original launch. Until then, Site Electricity lists reimbursement for manual review.`;
    case 'transition_scheduled':
      return v.notice
        ? `Transition Date ${formatCalendarDay(v.notice.transition_on)}. Notice sent ${formatCalendarDay(v.notice.sent_on)} by ${TRANSITION_METHOD_LABELS[v.notice.method].toLowerCase()}. The bank countdown starts that day or when a site manager can connect the bank, if later.`
        : 'A Transition Date is recorded and still ahead.';
    case 'enrolled': {
      const window = v.window_ends_at ? ` The 60 days to connect a bank run through ${lastDay(v.window_ends_at, zone)}.`
        : v.grace_anchor_at === null ? ' The bank window has not started because no site manager has bank access yet.' : '';
      if (v.start_basis === 'launch') {
        return `Its launch on ${launched ?? (v.start_at ? instantDay(v.start_at, zone) : 'record')} is the reimbursement Start Date under the Site Host Terms. No Transition Notice applies.${window}`;
      }
      const from = v.notice ? formatCalendarDay(v.notice.transition_on) : 'the Transition Date';
      if (v.notice && v.start_at && Date.parse(v.notice.transition_at) < Date.parse(v.start_at)) {
        return `The Site Host Terms apply from ${from}. The notice was recorded on ${instantDay(v.start_at, zone)}, so reimbursement starts then; sessions in between are on the Site Electricity page for manual reimbursement. The bank deadline uses its separate grace anchor.${window}`;
      }
      return `The Site Host Terms apply from ${from}.${window}`;
    }
    default:
      return '';
  }
}

export function LocationTransitionNoticeCard({ locationId }: { locationId: string }) {
  const qc = useQueryClient();
  const key = ['electricity-transition', locationId];
  const query = useQuery({ queryKey: key, queryFn: () => fetchElectricityTransition(locationId), staleTime: 30_000 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [basis, setBasis] = useState<TransitionBasis>('notice');
  const [sentOn, setSentOn] = useState('');
  const [method, setMethod] = useState<TransitionMethod>('email');
  const [transitionOn, setTransitionOn] = useState('');
  const [dateEdited, setDateEdited] = useState(false);
  const [note, setNote] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState('');
  const v = query.data;

  async function refreshAll() {
    await qc.invalidateQueries({ queryKey: key });
    await qc.invalidateQueries({ queryKey: ['location-bank'] });
    await qc.invalidateQueries({ queryKey: ['bank-readiness'] });
    await qc.invalidateQueries({ queryKey: ['site-electricity'] });
  }
  function start(view: ElectricityTransitionView) {
    const sent = view.notice?.sent_on ?? view.today;
    setBasis(view.notice?.basis ?? 'notice'); setSentOn(sent); setMethod(view.notice?.method ?? 'email');
    setTransitionOn(view.notice?.transition_on ?? defaultTransitionDate(sent)); setDateEdited(!!view.notice);
    setNote(view.notice?.note ?? ''); setError(''); setOpen(true);
  }
  function changeSent(value: string) {
    setSentOn(value);
    if (!dateEdited && basis === 'notice' && value) setTransitionOn(defaultTransitionDate(value));
  }
  function changeBasis(value: TransitionBasis) {
    setBasis(value);
    if (!dateEdited && sentOn) setTransitionOn(value === 'notice' ? defaultTransitionDate(sentOn) : sentOn);
  }
  async function save() {
    if (!v) return;
    const problem = transitionNoticeError({ basis, sent_on: sentOn, transition_on: transitionOn, note }, v.today);
    if (problem) { setError(problem); return; }
    setBusy(true); setError('');
    try {
      await recordElectricityTransition(locationId, { basis, sent_on: sentOn, method, note: note.trim(), transition_on: transitionOn });
      await refreshAll(); setOpen(false);
    } catch (err) { setError(await getDisplayErrorMessage(err, 'Could not record the Transition Notice.')); }
    finally { setBusy(false); }
  }
  async function withdraw() {
    if (!reason.trim()) { setError('Say why the notice is withdrawn.'); return; }
    setBusy(true); setError('');
    try { await withdrawElectricityTransition(locationId, reason.trim()); await refreshAll(); setWithdrawing(false); setReason(''); }
    catch (err) { setError(await getDisplayErrorMessage(err, 'Could not withdraw the Transition Notice.')); }
    finally { setBusy(false); }
  }

  if (query.isError) return null;
  return <section className={`${styles.bankCard} ${styles.card}`} aria-label="Site Host Terms transition">
    <div className={styles.cardHead}><FileSignature size={18} aria-hidden /><h2 className={styles.cardTitle}>Site Host Terms transition</h2></div>
    {!v ? <p>Loading...</p> : <>
      <p>{transitionStatusLine(v)}</p>
      {!v.site_host_terms.published && <p className={styles.muted}>The Site Host Terms are not published in the Platform yet. A Transition Notice can be recorded once they are; until then nothing enrolls.</p>}
      {v.site_host_terms_first_accepted_at && <p className={styles.muted}>A Building Admin accepted the Site Host Terms for this location on {instantDay(v.site_host_terms_first_accepted_at, v.location.transition_zone)}.</p>}
      {v.correctable_until && v.editable && <p className={styles.muted}>It can be corrected or withdrawn until {new Date(v.correctable_until).toLocaleTimeString()}.</p>}
      {v.can_edit && v.editable && v.site_host_terms.published && !open && !withdrawing && <p>
        <button type="button" className={form.btnSecondary} onClick={() => start(v)}>{v.notice ? 'Correct transition notice' : 'Record transition notice'}</button>{' '}
        {v.notice && <button type="button" className={form.btnSecondary} onClick={() => { setWithdrawing(true); setError(''); }}>Withdraw notice</button>}
      </p>}
      {open && <fieldset disabled={busy} style={{ border: '1px solid var(--evi-border)', padding: 12, minWidth: 0, display: 'grid', gap: 12 }}>
        <legend>{v.notice ? 'Correct transition notice' : 'Record transition notice'}</legend>
        <label className={form.field}>Basis<select className={form.input} value={basis} onChange={e => changeBasis(e.target.value as TransitionBasis)}>
          {(Object.keys(TRANSITION_BASIS_LABELS) as TransitionBasis[]).map(b => <option key={b} value={b}>{TRANSITION_BASIS_LABELS[b]}</option>)}
        </select></label>
        <label className={form.field}>Date sent<input className={form.input} type="date" min={TRANSITION_EARLIEST_DAY} max={v.today} value={sentOn}
          onChange={e => changeSent(e.target.value)} required /></label>
        <label className={form.field}>Method<select className={form.input} value={method} onChange={e => setMethod(e.target.value as TransitionMethod)}>
          {(Object.keys(TRANSITION_METHOD_LABELS) as TransitionMethod[]).map(m => <option key={m} value={m}>{TRANSITION_METHOD_LABELS[m]}</option>)}
        </select></label>
        <label className={form.field}>{basis === 'notice' ? 'Transition Date stated in the notice' : basis === 'acceptance' ? 'Date the Site Host accepted' : 'Date the amendment was signed'}
          <input className={form.input} type="date" value={transitionOn}
            min={basis === 'notice' && sentOn ? defaultTransitionDate(sentOn) : sentOn || TRANSITION_EARLIEST_DAY}
            max={basis === 'notice' ? undefined : v.today}
            onChange={e => { setTransitionOn(e.target.value); setDateEdited(true); }} required /></label>
        <label className={form.field}>Note (required): what was sent, to whom and where<textarea className={form.input} value={note} maxLength={2000}
          onChange={e => setNote(e.target.value)} /></label>
        <p className={styles.muted}>From the Transition Date the Site Host Terms apply here. The bank countdown starts at midnight that day in {v.location.time_zone ?? 'Pacific time (no time zone is recorded for this location)'} and runs 60 days. Sessions from that moment are tracked for reimbursement.</p>
        {transitionOn && transitionOn <= v.today && <p className={styles.warn}>This date has passed or is today. The Site Host Terms apply from it, but the ledger and the 60 days start when you save; sessions in between are listed on the Site Electricity page for manual reimbursement. You can correct or withdraw it for 50 minutes after saving.</p>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <button type="button" className={form.btnPrimary} onClick={() => void save()}>Save transition notice</button>
          <button type="button" className={form.btnSecondary} onClick={() => setOpen(false)}>Cancel</button>
        </div>
      </fieldset>}
      {withdrawing && <fieldset disabled={busy} style={{ border: '1px solid var(--evi-border)', padding: 12, minWidth: 0, display: 'grid', gap: 12 }}>
        <legend>Withdraw transition notice</legend>
        <p>The existing agreement applies again and the bank countdown is hidden.</p>
        <label className={form.field}>Reason (required)<textarea className={form.input} value={reason} maxLength={2000} onChange={e => setReason(e.target.value)} /></label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <button type="button" className={form.btnPrimary} onClick={() => void withdraw()}>Withdraw notice</button>
          <button type="button" className={form.btnSecondary} onClick={() => setWithdrawing(false)}>Cancel</button>
        </div>
      </fieldset>}
      {error && <p role="alert" className={styles.error}>{error}</p>}
      {v.launch.first_at && <p className={styles.muted}>First launch evidence: {SOURCE_LABELS[v.launch.source ?? 'commissioning']} on {instantDay(v.launch.first_at, v.location.transition_zone)}.</p>}
      {v.history.length > 0 && <details><summary>Transition history ({v.history.length})</summary>
        {v.history.map(h => <p key={`${h.at}-${h.action}`}>
          {h.action === 'recorded' ? 'Recorded' : h.action === 'corrected' ? 'Corrected' : 'Withdrawn'} {new Date(h.at).toLocaleString()} by {h.actor ?? 'unknown'}
          {h.after?.transition_on ? `. Transition Date ${formatCalendarDay(h.after.transition_on)}` : ''}{h.after?.note ? `. ${h.after.note}` : ''}
        </p>)}
      </details>}
    </>}
  </section>;
}
